/**
 * Dashboard quick views (Osh, 01/10/2026): one click to see unconfirmed, upcoming
 * (next N days) or ongoing shows, in the overview, the table and the calendar.
 * Mirrors backend events.quick-view.ts (`quickView` + `quickDays` on GET /getAllEvents).
 */
export type QuickViewKind = "unconfirmed" | "upcoming" | "ongoing";
export type QuickViewDays = 7 | 14 | 30 | 60;
export type QuickView = { view: QuickViewKind; days: QuickViewDays } | null;

export const QUICK_VIEW_DAYS: QuickViewDays[] = [7, 14, 30, 60];
export const DEFAULT_QUICK_VIEW_DAYS: QuickViewDays = 14;

export const QUICK_VIEW_OPTIONS: { view: QuickViewKind; label: string; hint: string }[] = [
  { view: "unconfirmed", label: "Unconfirmed", hint: "Sent to the client and not approved yet" },
  { view: "upcoming", label: "Upcoming", hint: "Starting in the next days" },
  { view: "ongoing", label: "Ongoing", hint: "Running today" },
];

const KINDS = QUICK_VIEW_OPTIONS.map((o) => o.view);

/** Saved filters are free JSON: anything malformed means "no quick view". */
export function sanitizeQuickView(input: unknown): QuickView {
  const v = input as { view?: unknown; days?: unknown } | null;
  if (!v || !KINDS.includes(v.view as QuickViewKind)) return null;
  const days = QUICK_VIEW_DAYS.includes(Number(v.days) as QuickViewDays)
    ? (Number(v.days) as QuickViewDays)
    : DEFAULT_QUICK_VIEW_DAYS;
  return { view: v.view as QuickViewKind, days };
}

/**
 * "Show duplicates" checkbox (2026-10-05): duplicates are hidden by default; checked,
 * they join the list struck through. GET /getAllEvents reads `includeDuplicates`.
 */
export function duplicatesToParams(show: boolean): { includeDuplicates?: string } {
  return show ? { includeDuplicates: "true" } : {};
}

/** Query params for GET /getAllEvents (table and calendar). */
export function quickViewToParams(q: QuickView): { quickView?: string; quickDays?: string } {
  if (!q) return {};
  return q.view === "upcoming" ? { quickView: q.view, quickDays: String(q.days) } : { quickView: q.view };
}

type QuickViewShow = { lifecycle: string; timing: string; daysUntilStart: number | null; isDuplicate?: boolean };

/**
 * Overview (board data): same meaning as the API, on the board's own fields
 * (computed by the backend in the show's timezone). Cancelled never matches.
 */
export function matchesQuickView(show: QuickViewShow, q: QuickView): boolean {
  if (!q) return true;
  if (show.lifecycle === "cancelled") return false;
  if (q.view === "ongoing") return show.timing === "ongoing";
  if (q.view === "upcoming") {
    const d = show.daysUntilStart;
    return show.timing === "upcoming" && d !== null && d >= 1 && d <= q.days;
  }
  return (show.lifecycle === "newRequest" || show.lifecycle === "pendingApproval") && show.timing !== "past";
}
